import React from "react";
import { Trash2, CheckCircle2, Save } from "lucide-react";
import { cn } from "../../lib/utils";
import { MapEditorEmptyState, MapEditorPaneHeader } from "./internal/MapEditorKitParts";
import type { 
  MapPresetPaneProps, 
  MapPresetItem
} from "../../contracts/map-workspace";

export function MapPresetPane({
  mode,
  presets,
  draftLabel = "Untitled Preset",
  saveCurrentLabel = "Save current state",
  onDraftLabelChange,
  onSaveCurrent,
  onApplyPreset,
  onDeletePreset,
  className,
}: MapPresetPaneProps) {
  const isAuthorMode = mode === "author";
  const isCustomizeMode = mode === "customize-save-copy";

  const renderPreset = (preset: MapPresetItem) => (
    <div
      key={preset.id}
      className={cn(
        "group flex flex-col gap-1 p-3 rounded-control border transition-all relative cursor-pointer",
        preset.active 
          ? "bg-primary/5 border-primary ring-1 ring-primary/20 shadow-sm" 
          : "bg-background border-border hover:border-primary/50",
        preset.disabled && "opacity-50 grayscale cursor-not-allowed pointer-events-none"
      )}
      onClick={() => !preset.disabled && onApplyPreset(preset.id)}
    >
      <div className="flex items-center justify-between">
        <h4 className={cn(
          "text-xs font-bold truncate pr-6",
          preset.active ? "text-primary" : "text-foreground"
        )}>
          {preset.label}
        </h4>
        {preset.active && (
          <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
        )}
        {!preset.active && !preset.disabled && onDeletePreset && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeletePreset(preset.id);
            }}
            className="opacity-0 group-hover:opacity-100 p-1.5 -mr-1.5 hover:bg-red-50 hover:text-red-500 rounded-full transition-all"
            title="Delete preset"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
      {preset.summary && (
        <p className="text-[10px] text-muted-foreground leading-snug line-clamp-2 italic">
          {preset.summary}
        </p>
      )}
    </div>
  );

  return (
    <div className={cn("flex flex-col w-full h-full bg-background overflow-hidden", className)}>
      <div className="p-4 h-full flex flex-col">
        <MapEditorPaneHeader
          title="Saved Views"
          description="Capture and revisit meaningful map states without leaving the workspace."
          badge={
            <span className="rounded-sm border bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase text-muted-foreground">
              {mode}
            </span>
          }
          className="mb-4"
        />

        {/* Draft Area (Author/Customize Mode) */}
        {(isAuthorMode || isCustomizeMode) && (
          <div className="flex flex-col gap-3 p-3 mb-6 bg-muted/30 border border-border rounded-lg shadow-inner">
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                New Preset Name
              </label>
              <input
                type="text"
                value={draftLabel}
                onChange={(e) => onDraftLabelChange?.(e.target.value)}
                placeholder="Enter name..."
                className="w-full px-2 py-1.5 text-xs bg-background border border-border rounded-control focus:ring-1 focus:ring-primary focus:outline-none transition-all font-medium"
              />
            </div>
            <button
              onClick={onSaveCurrent}
              className="w-full flex items-center justify-center gap-2 py-2 text-xs font-bold bg-primary text-primary-foreground rounded-control shadow-sm hover:bg-primary/90 active:scale-95 transition-all"
            >
              <Save className="w-3.5 h-3.5" />
              {saveCurrentLabel}
            </button>
          </div>
        )}

        {/* Preset List */}
        <div className="flex-1 flex flex-col gap-2 overflow-y-auto pr-1 -mr-1 scrollbar-thin scrollbar-thumb-muted">
          {presets.length === 0 ? (
            <MapEditorEmptyState
              title="No saved views yet"
              message="Save the current map state when you want teammates or viewers to return to it quickly."
            />
          ) : (
            presets.map(renderPreset)
          )}
        </div>

        {/* View Mode Footer Message */}
        {mode === "view" && presets.length > 0 && (
          <p className="mt-4 text-[10px] text-muted-foreground text-center italic">
            Select a preset to switch views
          </p>
        )}
      </div>
    </div>
  );
}
